import React, { lazy } from 'react';
import { Navigate } from 'react-router-dom';
import { Icon } from '@chakra-ui/react';
import { MdPerson, MdHome, MdOutlineShoppingCart, MdChat, MdSettings, MdBarChart, MdViewKanban, MdTaskAlt, MdCalendarMonth, MdEventRepeat, MdGridOn, MdSupportAgent } from 'react-icons/md';
import { FaRegNewspaper } from "react-icons/fa";
import { BsFillPersonLinesFill, BsPersonBoundingBox } from "react-icons/bs";
import { PiNewspaperLight } from "react-icons/pi";
import { SECCIONES_CONFIGURACION } from 'views/admin/Configuraciones/secciones';

// Admin Imports
const MainDashboard = lazy(() => import('views/admin/default'));
const Actividades = lazy(() => import('views/admin/Actividades'));
const Calendario = lazy(() => import('views/admin/Calendario'));
const CrearTicket = lazy(() => import('views/admin/CrearTicket'));
const Oportunidades = lazy(() => import('views/admin/Oportunidades'));
const Cotizaciones = lazy(() => import('views/admin/Cotizaciones'));
const CrearCotizaciones = lazy(() => import('views/admin/Cotizaciones/Crearcotizacion.js'));
const VerOrdendeCompra = lazy(() => import('views/admin/OrdenesCompra/verOrdendeCompra.js'));
const VerFactura = lazy(() => import('views/admin/Facturacion/verFactura.js'));
const VerRemision = lazy(() => import('views/admin/Remisiones/verRemision.js'));
const Facturacion = lazy(() => import('views/admin/Facturacion'));
const FacturasProgramadas = lazy(() => import('views/admin/FacturasProgramadas'));
const Remisiones = lazy(() => import('views/admin/Remisiones'));
const Clientes = lazy(() => import('views/admin/Clientes'));
const Contactos = lazy(() => import('views/admin/Contactos'));
const Proveedores = lazy(() => import('views/admin/Proveedores'));
const Ordenes = lazy(() => import('views/admin/OrdenesCompra'));
const Usuarios = lazy(() => import('views/admin/Usuarios'));
const Administracion = lazy(() => import('views/admin/Administracion'));
const ProductosPredeterminados = lazy(() => import('views/admin/ProductosPredeterminados'));
const Correo = lazy(() => import('views/admin/Correo'));
const Plantillas = lazy(() => import('views/admin/Plantillas'));
const WhatsApp = lazy(() => import('views/admin/WhatsApp'));
const WhatsAppMonitor = lazy(() => import('views/admin/WhatsAppMonitor'));
const Reportes = lazy(() => import('views/admin/Reportes'));
const Configuraciones = lazy(() => import('views/admin/Configuraciones'));
const HojasCalculo = lazy(() => import('views/admin/HojasCalculo'));
const EditorHojaCalculo = lazy(() => import('views/admin/HojasCalculo/Editor.jsx'));
const SignInCentered = lazy(() => import('views/auth/signIn'));

// Reportes IA, Modulos, Licencias y Jobs ya no tienen ítem propio en la barra lateral:
// se abren desde Configuraciones. Sus direcciones de antes siguen funcionando y llevan a
// su sección.
const redirigirAConfiguraciones = (name, path, seccionKey) => {
  const seccion = SECCIONES_CONFIGURACION.find((item) => item.key === seccionKey);
  return {
    name,
    layout: '/admin',
    path,
    icon: <Icon as={MdSettings} width="20px" height="20px" color="inherit" />,
    component: <Navigate to={`/admin/Configuraciones?seccion=${seccion.key}`} replace />,
    moduleKey: seccion.moduleKey,
    superAdminOnly: true,
    show: false,
  };
};



const routes = [
  {
    name: 'Crear ticket de soporte',
    layout: '/admin',
    path: '/Crear_Ticket',
    icon: <Icon as={MdSupportAgent} width="20px" height="20px" color="inherit" />,
    component: <CrearTicket />,
    moduleKey: 'principal',
    show: false,
  },
  {
    name: 'Principal',
    layout: '/admin',
    path: '/default',
    icon: <Icon as={MdHome} width="20px" height="20px" color="inherit" />,
    component: <MainDashboard />,
    moduleKey: 'principal',
    show: true,
  },
  {
    name: 'Mis tareas',
    layout: '/admin',
    path: '/Actividades',
    icon: <Icon as={MdTaskAlt} width="20px" height="20px" color="inherit" />,
    component: <Actividades />,
    moduleKey: 'actividades',
    show: true,
  },
  {
    // Se abre desde el botón de calendario del navbar, no desde el menú.
    name: 'Calendario',
    layout: '/admin',
    path: '/Calendario',
    icon: <Icon as={MdCalendarMonth} width="20px" height="20px" color="inherit" />,
    component: <Calendario />,
    moduleKey: 'actividades',
    show: false,
  },
  {
    name: 'Oportunidades',
    layout: '/admin',
    path: '/Oportunidades',
    icon: <Icon as={MdViewKanban} width="20px" height="20px" color="inherit" />,
    component: <Oportunidades />,
    moduleKey: 'oportunidades',
    show: true,
  },
  {
    name: 'Cotizaciones',
    layout: '/admin',
    path: '/Cotizaciones',
    icon: (
      <Icon as={MdOutlineShoppingCart} width="20px" height="20px" color="inherit" />
    ),
    component: <Cotizaciones />,
    secondary: true,
    moduleKey: 'cotizaciones',
    show: true,
  },
 
  {
    name: 'Remisiones',
    layout: '/admin',
    icon: <Icon as={FaRegNewspaper} width="20px" height="20px" color="inherit" />,
    path: '/Remisiones',
    component: <Remisiones />,
    moduleKey: 'remisiones',
    show: true,
  },
  {
    name: 'Facturacion',
    layout: '/admin',
    icon: <Icon as={FaRegNewspaper} width="20px" height="20px" color="inherit" />,
    path: '/Facturacion',
    component: <Facturacion />,
    moduleKey: 'facturacion',
    show: true,
  },
  {
    name: 'Facturas Programadas',
    layout: '/admin',
    icon: <Icon as={MdEventRepeat} width="20px" height="20px" color="inherit" />,
    path: '/Facturas_Programadas',
    component: <FacturasProgramadas />,
    moduleKey: 'facturas_programadas',
    show: true,
  },
  {
    name: 'Ordenes de Compra',
    layout: '/admin',
    icon: <Icon as={PiNewspaperLight} width="20px" height="20px" color="inherit" />,
    path: '/Ordenes_de_Compra',
    component: <Ordenes />,
    moduleKey: 'ordenes_compra',
    show: true,
  },
  {
    name: 'Clientes',
    layout: '/admin',
    icon: <Icon as={BsPersonBoundingBox} width="20px" height="20px" color="inherit" />,
    path: '/Clientes',
    component: <Clientes />,
    moduleKey: 'clientes',
    show: true,
  },

  {
    name: 'Contactos',
    layout: '/admin',
    icon: <Icon as={PiNewspaperLight} width="20px" height="20px" color="inherit" />,
    path: '/Contactos',
    component: <Contactos />,
    moduleKey: 'contactos',
    show: true,
  },

  {
    name: 'Proveedores',
    layout: '/admin',
    icon: <Icon as={BsFillPersonLinesFill} width="20px" height="20px" color="inherit" />,
    path: '/Proveedores',
    component: <Proveedores />,
    moduleKey: 'proveedores',
    show: true,
  },

  {
    name: 'Reportes',
    layout: '/admin',
    icon: <Icon as={MdBarChart} width="20px" height="20px" color="inherit" />,
    path: '/Reportes',
    component: <Reportes />,
    moduleKey: 'reportes',
    show: true,
  },
  {
    name: 'Hojas de cálculo',
    layout: '/admin',
    icon: <Icon as={MdGridOn} width="20px" height="20px" color="inherit" />,
    path: '/Hojas_Calculo',
    component: <HojasCalculo />,
    moduleKey: 'hojas_calculo',
    show: true,
  },
  {
    // Se abre desde el listado con ?id=.
    name: 'Hoja de cálculo',
    layout: '/admin',
    icon: <Icon as={MdGridOn} width="20px" height="20px" color="inherit" />,
    path: '/Hoja_Calculo',
    component: <EditorHojaCalculo />,
    moduleKey: 'hojas_calculo',
    secondary: true,
    show: false,
  },
  {
    name: 'Usuarios',
    layout: '/admin',
    path: '/Usuarios',
    icon: <Icon as={MdPerson} width="20px" height="20px" color="inherit" />,
    component: <Usuarios />,
    moduleKey: 'usuarios',
    show: true,
  },
  {
    name: 'Administracion',
    layout: '/admin',
    path: '/Administracion',
    icon: <Icon as={MdPerson} width="20px" height="20px" color="inherit" />,
    component: <Administracion />,
    moduleKey: 'administracion',
    show: true,
  },
  {
    name: 'Productos Predeterminados',
    layout: '/admin',
    path: '/Productos_Predeterminados',
    icon: <Icon as={MdOutlineShoppingCart} width="20px" height="20px" color="inherit" />,
    component: <ProductosPredeterminados />,
    moduleKey: 'productos_predeterminados',
    show: true,
  },
  {
    name: 'WhatsApp',
    layout: '/admin',
    path: '/WhatsApp',
    icon: <Icon as={MdChat} width="20px" height="20px" color="inherit" />,
    component: <WhatsApp />,
    moduleKey: 'whatsapp',
    show: true,
  },
  {
    name: 'WhatsApp Monitor',
    layout: '/admin',
    path: '/WhatsApp_Monitor',
    icon: <Icon as={MdChat} width="20px" height="20px" color="inherit" />,
    component: <WhatsAppMonitor />,
    moduleKey: 'whatsapp',
    show: false,
    secondary: true,
  },
  {
    name: 'InicioSesion',
    layout: '/auth',
    path: '/sign-in',
    icon: <Icon as={MdPerson} width="20px" height="20px" color="inherit" />,
    component: <SignInCentered />,
    show: false
  },
  {
    name: 'Crear Cotización',
    layout: '/admin',
    path: '/Nueva_Cotizacion',
    icon: (
      <Icon as={MdOutlineShoppingCart} width="20px" height="20px" color="inherit" />
    ),
    component: <CrearCotizaciones />,
    secondary: true,
    moduleKey: 'cotizaciones',
    show: false
  },
  {
    name: 'ver Orden de Compra',
    layout: '/admin',
    path: '/Ver_Orden_de_Compra',
    icon: (
      <Icon as={MdOutlineShoppingCart} width="20px" height="20px" color="inherit" />
    ),
    component: <VerOrdendeCompra />,
    secondary: true,
    moduleKey: 'ordenes_compra',
    show: false
  },
  {
    name: 'ver Remision',
    layout: '/admin',
    path: '/Ver_Remision',
    icon: (
      <Icon as={MdOutlineShoppingCart} width="20px" height="20px" color="inherit" />
    ),
    component: <VerRemision />,
    secondary: true,
    moduleKey: 'remisiones',
    show: false
  },
  {
    name: 'ver Factura',
    layout: '/admin',
    path: '/Ver_Factura',
    icon: (
      <Icon as={MdOutlineShoppingCart} width="20px" height="20px" color="inherit" />
    ),
    component: <VerFactura />,
    secondary: true,
    moduleKey: 'facturacion',
    show: false
  },
  {
    name: 'Correo',
    layout: '/admin',
    path: '/Envio_Correo',
    icon: (
      <Icon as={MdChat} width="20px" height="20px" color="inherit" />
    ),
    component: <Correo />,
    secondary: true,
    moduleKey: 'correo',
    show: true,
  },
  {
    name: 'Plantillas',
    layout: '/admin',
    path: '/Plantillas',
    icon: (
      <Icon as={MdSettings} width="20px" height="20px" color="inherit" />
    ),
    component: <Plantillas />,
    moduleKey: 'plantillas',
    show: true,
  },
  {
    name: 'Configuraciones',
    layout: '/admin',
    path: '/Configuraciones',
    icon: <Icon as={MdSettings} width="20px" height="20px" color="inherit" />,
    component: <Configuraciones />,
    // Se abre con tener cualquiera de los módulos que reúne, y solo siendo Super
    // Administrador.
    moduleKey: SECCIONES_CONFIGURACION.map((seccion) => seccion.moduleKey),
    superAdminOnly: true,
    // Va anclada al pie de la barra lateral, encima de la versión, no en la lista.
    pinned: true,
    show: true,
  },
  redirigirAConfiguraciones('IA Reportes', '/IA_Reportes', 'reportes-ia'),
  redirigirAConfiguraciones('Modulos', '/Modulos', 'modulos'),
  redirigirAConfiguraciones('Licencias', '/Licencias', 'licencias'),
  redirigirAConfiguraciones('Jobs', '/Jobs', 'jobs'),
];

export default routes;
